/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import Spinner from './Spinner';
import { useTranslation } from '../i18n/useTranslation';
import CalendarPicker from './CalendarPicker';
import { BookingDetails } from '../types';
import { portfolioItems } from '@shared/data/portfolioData';
import { DiscountService } from '../services/discountService';
import { useLocalizedPrice } from '../utils/priceUtils';

interface BookingFormState {
  serviceIds: string[];
  dateTime: Date | null;
  name: string;
  email: string;
  phone: string;
  notes: string;
}

interface BookingFormProps {
  onSubmit: (details: BookingDetails) => Promise<void>;
  isLoading: boolean;
  isSuccess: boolean;
  error: string | null;
  resetForm: () => void;
  initialNotes?: string;
}

const BookingForm: React.FC<BookingFormProps> = ({ onSubmit, isLoading, isSuccess, error, resetForm, initialNotes }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<BookingFormState>({
    serviceIds: [],
    dateTime: null,
    name: '',
    email: '',
    phone: '',
    notes: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof BookingFormState, string>>>({});

  const { formatPrice } = useLocalizedPrice();

  const services = useMemo(() =>
    portfolioItems.map(item => {
      // Find the cheapest active tier or first tier
      const baseTier = item.pricingTiers.find(t => t.basePriceEUR) || item.pricingTiers[0];
      const basePrice = baseTier?.basePriceEUR || 0;

      const discount = DiscountService.calculateDiscount(basePrice, item.id);

      const priceLabel = basePrice ? ` (${formatPrice(discount.discountedPrice)})` : '';
      const discountNote = discount.appliedCampaign ? ` [${t(discount.appliedCampaign.nameKey)}]` : '';

      return {
        value: item.id,
        label: `${t(item.titleKey)}${priceLabel}${discountNote}`
      };
    }), [t, formatPrice]);

  useEffect(() => {
    if (initialNotes) {
      setFormData(prev => ({ ...prev, notes: initialNotes }));
    }

    // Check for service query param
    const params = new URLSearchParams(window.location.search);
    const serviceParam = params.get('service');
    if (serviceParam) {
      setFormData(prev => {
        const newServiceIds = new Set(prev.serviceIds);
        newServiceIds.add(serviceParam);
        return { ...prev, serviceIds: Array.from(newServiceIds) };
      });
    }
  }, [initialNotes]);

  const validate = useCallback(() => {
    const errors: Partial<Record<keyof BookingFormState, string>> = {};
    if (formData.serviceIds.length === 0) errors.serviceIds = t('bookingForm_error_service');
    if (!formData.dateTime) errors.dateTime = t('bookingForm_error_dateTime');
    if (!formData.name.trim()) errors.name = t('bookingForm_error_name');
    if (!formData.email.trim()) errors.email = t('bookingForm_error_email');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = t('bookingForm_error_email_invalid');
    if (formData.phone && !/^\+?[0-9\s-()]{7,20}$/.test(formData.phone)) {
      errors.phone = t('bookingForm_error_phone_invalid');
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors(prev => ({ ...prev, [name]: undefined }));
  }, []);

  const handleServiceChange = (serviceId: string) => {
    setFormData(prev => {
      const newServiceIds = new Set(prev.serviceIds);
      if (newServiceIds.has(serviceId)) {
        newServiceIds.delete(serviceId);
      } else {
        newServiceIds.add(serviceId);
      }
      return { ...prev, serviceIds: Array.from(newServiceIds) };
    });
    setFormErrors(prev => ({ ...prev, serviceIds: undefined }));
  };

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      const submissionData: BookingDetails = {
        serviceIds: formData.serviceIds,
        date: formData.dateTime!.toISOString().split('T')[0],
        time: formData.dateTime!.toTimeString().split(' ')[0].substring(0, 5),
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        notes: formData.notes,
      };
      await onSubmit(submissionData);
    }
  }, [formData, onSubmit, validate]);

  useEffect(() => {
    if (isSuccess) {
      setFormData({
        serviceIds: [],
        dateTime: null,
        name: '',
        email: '',
        phone: '',
        notes: '',
      });
      setFormErrors({});
    }
  }, [isSuccess]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md dark:bg-red-900/20 dark:border-red-600 dark:text-red-300"
          role="alert"
        >
          <p className="font-bold">{t('bookingForm_error_title')}</p>
          <p>{error}</p>
        </motion.div>
      )}

      {isSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-md dark:bg-green-900/20 dark:border-green-600 dark:text-green-300"
          role="alert"
        >
          <p className="font-bold">{t('bookingForm_success_title')}</p>
          <p className="mb-2">{t('bookingForm_success_message_email') || 'Your booking request has been submitted. Please check your email for the calendar invitation.'}</p>
          <div className="flex flex-col sm:flex-row mt-4">
            <button
              type="button"
              onClick={resetForm}
              className="flex-1 bg-green-700 text-white font-semibold py-2 px-4 rounded-md text-sm hover:bg-green-800 transition-colors dark:bg-green-800 dark:hover:bg-green-700"
            >
              {t('bookingForm_success_cta')}
            </button>
          </div>
        </motion.div>
      )}

      {!isSuccess && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('bookingForm_services_label')}</label>
            <div className={`space-y-2 max-h-48 overflow-y-auto custom-scrollbar border rounded-lg p-3 ${formErrors.serviceIds ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}>
              {services.map(s => (
                <label key={s.value} className="flex items-center space-x-3 cursor-pointer p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.serviceIds.includes(s.value)}
                    onChange={() => handleServiceChange(s.value)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-800 dark:text-gray-200">{s.label}</span>
                </label>
              ))}
            </div>
            {formErrors.serviceIds && <p id="service-error" className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.serviceIds}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('bookingForm_date_label')}</label>
            <CalendarPicker
              value={formData.dateTime}
              onChange={(date) => {
                setFormData(prev => ({ ...prev, dateTime: date }));
                setFormErrors(prev => ({ ...prev, dateTime: undefined }));
              }}
              minDate={new Date()}
            />
            {formErrors.dateTime && <p id="dateTime-error" className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.dateTime}</p>}
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('bookingForm_name_label')}</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('bookingForm_name_placeholder')}
              disabled={isLoading}
              className={`w-full p-3 border rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400 ${formErrors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
              aria-required="true"
              aria-invalid={!!formErrors.name}
              aria-describedby={formErrors.name ? 'name-error' : undefined}
            />
            {formErrors.name && <p id="name-error" className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.name}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('bookingForm_email_label')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('bookingForm_email_placeholder')}
                disabled={isLoading}
                className={`w-full p-3 border rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400 ${formErrors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                aria-required="true"
                aria-invalid={!!formErrors.email}
                aria-describedby={formErrors.email ? 'email-error' : undefined}
              />
              {formErrors.email && <p id="email-error" className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.email}</p>}
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('bookingForm_phone_label')}</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={t('bookingForm_phone_placeholder')}
                disabled={isLoading}
                className={`w-full p-3 border rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400 ${formErrors.phone ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                aria-required="false"
                aria-invalid={!!formErrors.phone}
                aria-describedby={formErrors.phone ? 'phone-error' : undefined}
              />
              {formErrors.phone && <p id="phone-error" className="mt-1 text-sm text-red-600 dark:text-red-400">{formErrors.phone}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('bookingForm_notes_label')}</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={5}
              placeholder={t('bookingForm_notes_placeholder')}
              disabled={isLoading}
              className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:placeholder-gray-400 resize-y"
              aria-multiline="true"
            ></textarea>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center bg-gray-900 text-white font-semibold py-3 px-5 rounded-lg transition-colors duration-200 ease-in-out hover:bg-gray-700 active:scale-95 text-lg disabled:opacity-50 disabled:cursor-not-allowed dark:bg-indigo-600 dark:hover:bg-indigo-500"
          >
            {isLoading && <Spinner className="w-5 h-5 mr-3 text-white" />}
            {isLoading ? t('bookingForm_submitButton_loading') : t('bookingForm_submitButton')}
          </button>
        </>
      )}
    </form>
  );
};

export default BookingForm;